import { DualSelectList, Icons } from '@silverfort/sf-components';
import { AD_ENTITY_TYPES, HTTP_OPTIONS } from '@silverfort-nodelibs/common/utils/consts';
import { httpGet } from '../../components/utils';
import { FC, HTMLAttributes, useCallback, useEffect, useMemo, useState } from 'react';
import { DualListItemProps } from '@silverfort/sf-components/dist/DualSelectList/DualSelectListUtils';

export type entity = {
    guid: string;
    name: string;
    domain_name: string;
    path: string;
    is_protected: boolean;
    is_deleted: boolean;
    is_disabled: boolean;
    type: number;
    identifier: string;
};

const listEntitiesTypes = {
    [AD_ENTITY_TYPES.USER]: {
        tooltip: 'Account',
        icon: Icons.User
    },
    [AD_ENTITY_TYPES.GROUP]: {
        tooltip: 'Security group',
        icon: Icons.UserGroup
    },
    [AD_ENTITY_TYPES.OU]: {
        tooltip: 'Organizational unit',
        icon: Icons.Folder
    },
    [AD_ENTITY_TYPES.SERVER]: {
        tooltip: 'Server',
        icon: Icons.Server
    }
};

export enum ENTITIES_OPTIONS {
    USERS = 'Accounts',
    GROUPS = 'Groups',
    OUS = 'OUs',
    SERVERS = 'Servers'
}

export enum TAB_OPTIONS {
    ALL = 'All',
    USERS = 'Accounts',
    SERVERS = 'Servers',
    GROUPS = 'Groups',
    OUS = 'OUs'
}

type TabOption = {
    name: string;
    value: TAB_OPTIONS;
    type: number[];
};

const baseTabsOptions: TabOption[] = [
    {
        name: 'All',
        value: TAB_OPTIONS.ALL,
        type: []
    },
    { name: 'Accounts', value: TAB_OPTIONS.USERS, type: [AD_ENTITY_TYPES.USER] },
    { name: 'Servers', value: TAB_OPTIONS.SERVERS, type: [AD_ENTITY_TYPES.SERVER] },
    { name: 'Groups', value: TAB_OPTIONS.GROUPS, type: [AD_ENTITY_TYPES.GROUP] },
    { name: 'OUs', value: TAB_OPTIONS.OUS, type: [AD_ENTITY_TYPES.OU] }
];

type EntitiesDualSelectListProps = {
    selectedGuids: string[];
    menuProps?: {
        anchorEl: HTMLButtonElement | undefined;
        open: boolean;
        anchorOrigin: {
            vertical: number | 'top' | 'center' | 'bottom';
            horizontal: number | 'center' | 'left' | 'right';
        };
        transformOrigin: {
            vertical: number | 'top' | 'center' | 'bottom';
            horizontal: number | 'center' | 'left' | 'right';
        };
    };
    onClose: () => void;
    onSelect: (selectedItems: entity[] | null) => void;
    triggerProps?: Omit<HTMLAttributes<HTMLElement>, 'onChange'>;
    TriggerComponent?: FC<Omit<HTMLAttributes<HTMLElement>, 'onChange'>>;
    entitiesTabsOptions?: TAB_OPTIONS[];
    title: string;
};

type TabsAndTypes = { tabs: TabOption[]; entitiesTypes: number[] };

const EntitiesDualSelectList: FC<EntitiesDualSelectListProps> = ({
    selectedGuids,
    onClose,
    onSelect,
    menuProps,
    triggerProps,
    TriggerComponent,
    entitiesTabsOptions,
    title
}) => {
    const [selected, setSelected] = useState<DualListItemProps[]>([]);

    const {tabs, entitiesTypes } = useMemo<TabsAndTypes>(() => {
        if (!entitiesTabsOptions?.length) return {tabs: [], entitiesTypes: []};

        const filtered = baseTabsOptions.filter((t) => entitiesTabsOptions.includes(t.value));
        if (!filtered.some((t) => t.value === TAB_OPTIONS.ALL)) return {tabs: filtered, entitiesTypes: []};

        const union = [...new Set(filtered.filter((t) => t.value !== TAB_OPTIONS.ALL).flatMap((t) => t.type))];

        const computedTabs = filtered.map((t) => (t.value === TAB_OPTIONS.ALL ? { ...t, type: union } : t));

        return {tabs: computedTabs, entitiesTypes: union};
    }, [entitiesTabsOptions]);


    useEffect(() => {
        const fetchSelected = async () => {
            const entityData: {
                data: entity[] | null;
            } = await httpGet(`/users/entitiesData`, {
                [HTTP_OPTIONS.REQ_OPTIONS]: {
                    params: {
                        guids: selectedGuids,
                        entitiesTypes: entitiesTypes
                    }
                }
            });

            const selectedItems = entityData.data?.map((o) => ({
                title: o.name + `${o.is_disabled ? ' (disabled)' : ''}`,
                value: o.guid,
                subtitle: `(${o.domain_name})`,
                IconLeft: listEntitiesTypes[o.type]?.icon,
                titles: {
                    display: o.name + `${o.is_disabled ? ' (disabled)' : ''}`,
                    full: o.path + `${o.is_disabled ? ' (disabled)' : ''}`
                }
            })) || [];
            setSelected(selectedItems);
        };
        if (selectedGuids.length) {
            fetchSelected();
        } else {
            setSelected([]);
        }
    }, [selectedGuids, entitiesTypes]);

    const getEntityOptions = useCallback(
        async (searchQuery: string, tabValue?: string) => {
            if (!searchQuery) {
                return [];
            }
            const entityData: {
                data: {
                    Results: entity[];
                } | null;
            } = await httpGet(`/users/searchEntitiesByTypes/${searchQuery}`, {
                [HTTP_OPTIONS.REQ_OPTIONS]: {
                    params: {
                        hideDeleted: true,
                        entitiesTypes: (tabs?.find((x) => x.value === tabValue) || tabs?.[0])?.type || [],
                        authTypes: ['ad']
                    }
                }
            });

            if (!entityData.data || !entityData.data.Results) {
                return [];
            }

            return (
                entityData.data?.Results.map((o) => ({
                    title: o.name + `${o.is_disabled ? ' (disabled)' : ''}`,
                    titles: {
                        display: o.name + `${o.is_disabled ? ' (disabled)' : ''}`,
                        full: o.path + `${o.is_disabled ? ' (disabled)' : ''}`
                    },
                    value: o.guid,
                    subtitle: `(${o.domain_name})`,
                    IconLeft: listEntitiesTypes[o.type]?.icon
                })) || []
            );
        },
        [tabs]
    );

    const onchange = async (guids) => {
        if (guids.length > 0) {
            const entityData = await httpGet(`/users/entitiesData`, {
                [HTTP_OPTIONS.REQ_OPTIONS]: {
                    params: {
                        guids: guids,
                        entitiesTypes: entitiesTypes
                    }
                }
            });
            onSelect(entityData.data);
        } else {
            onSelect([]);
        }
    };

    return (
        <DualSelectList
            onChange={onchange}
            id={'dual_select_list'}
            menuPopupClassName="top-0 right-0 z-[5000]"
            getItems={getEntityOptions}
            title={title}
            searchable={true}
            menuProps={menuProps}
            tabs={tabs}
            selectedItems={selected}
            multiple
            onClose={onClose}
            triggerProps={triggerProps}
            TriggerComponent={TriggerComponent}
            titleViewDropdownItems={[
                {
                    title: 'Display Name',
                    value: 'display'
                },
                {
                    title: 'Full Path',
                    value: 'full'
                }
            ]}
        />
    );
};

export default EntitiesDualSelectList;
